import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiUsers, FiMoreVertical, FiEdit2, FiTrash2, FiUserPlus, FiX, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import * as teamsApi from '../../api/teams';
import * as usersApi from '../../api/users';
import { useAuth } from '../../context/AuthContext';

type Team = teamsApi.Team;
type TeamMember = teamsApi.TeamMember;
type OrgUser = usersApi.OrgUser;

const COLOR_OPTIONS = [
  { id: 'blue', label: 'Bleu', bg: 'bg-blue-500', ring: 'ring-blue-500' },
  { id: 'red', label: 'Rouge', bg: 'bg-red-500', ring: 'ring-red-500' },
  { id: 'green', label: 'Vert', bg: 'bg-green-500', ring: 'ring-green-500' },
  { id: 'purple', label: 'Violet', bg: 'bg-purple-500', ring: 'ring-purple-500' },
  { id: 'orange', label: 'Orange', bg: 'bg-orange-500', ring: 'ring-orange-500' },
  { id: 'pink', label: 'Rose', bg: 'bg-pink-500', ring: 'ring-pink-500' },
];

const COLOR_CLASSES: Record<string, { bg: string; text: string; iconBg: string; iconText: string }> = {
  blue:   { bg: 'bg-blue-500',   text: 'text-blue-700',   iconBg: 'bg-blue-100',   iconText: 'text-blue-600' },
  red:    { bg: 'bg-red-500',    text: 'text-red-700',    iconBg: 'bg-red-100',    iconText: 'text-red-600' },
  green:  { bg: 'bg-green-500',  text: 'text-green-700',  iconBg: 'bg-green-100',  iconText: 'text-green-600' },
  purple: { bg: 'bg-purple-500', text: 'text-purple-700', iconBg: 'bg-purple-100', iconText: 'text-purple-600' },
  orange: { bg: 'bg-orange-500', text: 'text-orange-700', iconBg: 'bg-orange-100', iconText: 'text-orange-600' },
  pink:   { bg: 'bg-pink-500',   text: 'text-pink-700',   iconBg: 'bg-pink-100',   iconText: 'text-pink-600' },
};

const TeamsPage: React.FC = () => {
  const { t } = useTranslation('settings');
  const { user: currentUser } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [managingTeam, setManagingTeam] = useState<Team | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const canManage = currentUser?.role === 'owner' || currentUser?.role === 'admin';

  const showToast = useCallback((type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadTeams = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await teamsApi.listTeams();
      setTeams(data);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Impossible de charger les equipes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTeams(); }, [loadTeams]);

  const handleDelete = async (team: Team) => {
    if (!window.confirm(`Supprimer l'equipe "${team.name}" ?`)) return;
    try {
      await teamsApi.deleteTeam(team.id);
      showToast('success', 'Equipe supprimee');
      loadTeams();
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Erreur');
    }
    setActiveMenu(null);
  };

  const cc = (color: string) => COLOR_CLASSES[color] || COLOR_CLASSES.blue;

  return (
    <div className="p-8">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          {toast.type === 'success' ? <FiCheck size={16} /> : <FiAlertCircle size={16} />}
          {toast.msg}
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('teams.header.title')}</h2>
          <p className="text-gray-600 mt-1">{t('teams.header.subtitle')}</p>
        </div>
        {canManage && (
          <button onClick={() => { setEditingTeam(null); setShowModal(true); }}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition shadow-md">
            <FiPlus size={18} /> {t('teams.buttons.newTeam')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <FiAlertCircle size={32} className="mx-auto text-red-400 mb-2" />
          <p className="text-red-700">{error}</p>
          <button onClick={loadTeams} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm">Reessayer</button>
        </div>
      ) : teams.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <FiUsers size={64} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('teams.empty.title')}</h3>
          <p className="text-gray-500 mb-6">{t('teams.empty.subtitle')}</p>
          {canManage && (
            <button onClick={() => setShowModal(true)} className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700">
              <FiPlus size={20} /> {t('teams.buttons.createFirstTeam')}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {teams.map((team) => {
            const c = cc(team.color);
            const leads = team.members.filter(m => m.role === 'lead');
            return (
              <div key={team.id} className="bg-white rounded-xl border border-gray-200 hover:shadow-lg transition overflow-hidden">
                <div className={`h-2 ${c.bg}`} />
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`w-12 h-12 rounded-full ${c.iconBg} flex items-center justify-center flex-shrink-0`}>
                        <FiUsers className={c.iconText} size={22} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-900 truncate">{team.name}</h3>
                        <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{team.description}</p>
                      </div>
                    </div>
                    {canManage && (
                      <div className="relative">
                        <button onClick={() => setActiveMenu(activeMenu === team.id ? null : team.id)} className="p-2 hover:bg-gray-100 rounded-full">
                          <FiMoreVertical className="text-gray-600" />
                        </button>
                        {activeMenu === team.id && (
                          <div className="absolute right-0 mt-2 w-52 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10">
                            <button onClick={() => { setManagingTeam(team); setActiveMenu(null); }}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                              <FiUserPlus size={16} /> Gerer les membres
                            </button>
                            <button onClick={() => { setEditingTeam(team); setShowModal(true); setActiveMenu(null); }}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700">
                              <FiEdit2 size={16} /> Modifier
                            </button>
                            <button onClick={() => handleDelete(team)}
                              className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-red-600">
                              <FiTrash2 size={16} /> Supprimer
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-0.5">Membres</p>
                      <p className="text-2xl font-bold text-gray-900">{team.member_count}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-0.5">Responsables</p>
                      <p className="text-2xl font-bold text-gray-900">{leads.length}</p>
                    </div>
                  </div>

                  {leads.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-500 mb-1.5">Responsables</p>
                      <div className="space-y-1">
                        {leads.slice(0, 2).map(m => (
                          <div key={m.id} className="flex items-center gap-2">
                            <div className={`w-6 h-6 rounded-full ${c.iconBg} flex items-center justify-center text-xs font-medium ${c.iconText}`}>
                              {m.full_name.charAt(0)}
                            </div>
                            <span className="text-sm text-gray-700 truncate">{m.full_name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <button onClick={() => setManagingTeam(team)}
                    className="mt-2 w-full py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm font-medium">
                    Voir les membres
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <TeamModal
          team={editingTeam}
          onClose={() => { setShowModal(false); setEditingTeam(null); }}
          onSave={async (data) => {
            try {
              if (editingTeam) {
                await teamsApi.updateTeam(editingTeam.id, data);
                showToast('success', 'Equipe mise a jour');
              } else {
                await teamsApi.createTeam(data as any);
                showToast('success', 'Equipe creee');
              }
              setShowModal(false);
              setEditingTeam(null);
              loadTeams();
            } catch (e: any) {
              showToast('error', e.response?.data?.error || 'Erreur');
            }
          }}
        />
      )}

      {managingTeam && (
        <MembersModal
          team={managingTeam}
          canManage={canManage}
          onClose={() => { setManagingTeam(null); loadTeams(); }}
          showToast={showToast}
        />
      )}
    </div>
  );
};

const TeamModal: React.FC<{
  team: Team | null;
  onClose: () => void;
  onSave: (data: { name: string; description: string; color: string }) => void;
}> = ({ team, onClose, onSave }) => {
  const [name, setName] = useState(team?.name || '');
  const [description, setDescription] = useState(team?.description || '');
  const [color, setColor] = useState(team?.color || 'blue');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">{team ? "Modifier l'equipe" : "Nouvelle equipe"}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><FiX size={20} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave({ name, description, color }); }} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Couleur</label>
            <div className="flex gap-3">
              {COLOR_OPTIONS.map(c => (
                <button key={c.id} type="button" onClick={() => setColor(c.id)}
                  className={`w-9 h-9 rounded-full ${c.bg} transition-all ${color === c.id ? `ring-4 ring-offset-2 ${c.ring} scale-110` : 'hover:scale-105'}`}
                  title={c.label} />
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Annuler</button>
            <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">{team ? 'Enregistrer' : 'Creer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MembersModal: React.FC<{
  team: Team;
  canManage: boolean;
  onClose: () => void;
  showToast: (type: 'success' | 'error', msg: string) => void;
}> = ({ team, canManage, onClose, showToast }) => {
  const [members, setMembers] = useState<TeamMember[]>(team.members);
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState<'lead' | 'member'>('member');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (canManage) {
      usersApi.listUsers().then(users => {
        const memberIds = new Set(members.map(m => m.id));
        setOrgUsers(users.filter(u => !memberIds.has(u.id)));
      }).catch(() => {});
    }
  }, [canManage, members]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUserId) return;
    setSaving(true);
    try {
      const newMember = await teamsApi.addTeamMember(team.id, selectedUserId, selectedRole);
      setMembers(prev => [...prev, newMember]);
      setOrgUsers(prev => prev.filter(u => u.id !== selectedUserId));
      setSelectedUserId('');
      setShowAdd(false);
      showToast('success', 'Membre ajoute');
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (member: TeamMember) => {
    try {
      await teamsApi.removeTeamMember(team.id, member.id);
      setMembers(prev => prev.filter(m => m.id !== member.id));
      showToast('success', 'Membre retire');
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Erreur');
    }
  };

  const handleChangeRole = async (member: TeamMember, role: 'lead' | 'member') => {
    try {
      await teamsApi.updateTeamMemberRole(team.id, member.id, role);
      setMembers(prev => prev.map(m => m.id === member.id ? { ...m, role } : m));
    } catch (e: any) {
      showToast('error', 'Erreur lors de la mise a jour du role');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 flex-shrink-0 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{team.name}</h2>
            <p className="text-sm text-gray-500 mt-0.5">{members.length} membre{members.length !== 1 ? 's' : ''}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><FiX size={24} className="text-gray-600" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {canManage && !showAdd && (
            <button onClick={() => setShowAdd(true)}
              className="w-full mb-4 py-3 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-blue-500 hover:text-blue-600 transition flex items-center justify-center gap-2">
              <FiUserPlus size={18} /> Ajouter un membre
            </button>
          )}

          {showAdd && (
            <form onSubmit={handleAdd} className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Utilisateur</label>
                <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)} required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900">
                  <option value="">Selectionner un utilisateur...</option>
                  {orgUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.fullName} ({u.email})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role dans l'equipe</label>
                <select value={selectedRole} onChange={e => setSelectedRole(e.target.value as 'lead' | 'member')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900">
                  <option value="member">Membre</option>
                  <option value="lead">Responsable</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowAdd(false)} className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm">Annuler</button>
                <button type="submit" disabled={saving} className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50">
                  {saving ? 'Ajout...' : 'Ajouter'}
                </button>
              </div>
            </form>
          )}

          <div className="space-y-2">
            {members.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <FiUsers size={40} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm">Aucun membre dans cette equipe</p>
              </div>
            ) : members.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm">
                    {member.full_name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{member.full_name}</p>
                    <p className="text-sm text-gray-500">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {canManage ? (
                    <select value={member.role} onChange={e => handleChangeRole(member, e.target.value as 'lead' | 'member')}
                      className="px-3 py-1 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white">
                      <option value="member">Membre</option>
                      <option value="lead">Responsable</option>
                    </select>
                  ) : (
                    <span className="text-sm text-gray-600">{member.role === 'lead' ? 'Responsable' : 'Membre'}</span>
                  )}
                  {canManage && (
                    <button onClick={() => handleRemove(member)} className="p-2 text-red-600 hover:bg-red-50 rounded-full transition">
                      <FiTrash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex-shrink-0">
          <button onClick={onClose} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">Fermer</button>
        </div>
      </div>
    </div>
  );
};

export default TeamsPage;
